import React, { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { FileBarChart2, FileDown, TrendingUp, TrendingDown, Scale } from "lucide-react";
import { useApp, formatBRL } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { gerarDrePDF, gerarContasPagarPDF, gerarContasReceberPDF } from "../utils/financeiroPdf";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
// Período (regime de caixa): usa data de pagamento; se ausente, cai no mês de referência.
const periodoReceita = (f) => (f.dataPagamento ? f.dataPagamento.slice(0, 7) : f.mes);

const RelatoriosFinanceiros = () => {
  const { faturas, contasPagar, fornecedores, residentes, empresa } = useApp();
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(String(anoAtual));

  const resById = useMemo(() => Object.fromEntries((residentes || []).map((r) => [r.id, r])), [residentes]);
  const fornById = useMemo(() => Object.fromEntries((fornecedores || []).map((f) => [f.id, f])), [fornecedores]);

  const anosDisponiveis = useMemo(() => {
    const set = new Set([String(anoAtual)]);
    (faturas || []).forEach((f) => { if (f.paga && !f.cancelada) set.add(periodoReceita(f).slice(0, 4)); });
    (contasPagar || []).forEach((c) => { if (c.status === "pago" && c.dataPagamento) set.add(c.dataPagamento.slice(0, 4)); });
    return [...set].filter(Boolean).sort((a, b) => b.localeCompare(a));
  }, [faturas, contasPagar, anoAtual]);

  const dre = useMemo(() => {
    const meses = Array.from({ length: 12 }, (_, i) => ({ mes: i + 1, label: MESES[i], receitas: 0, despesas: 0, resultado: 0 }));
    const despesasPorCategoria = {};
    (faturas || []).forEach((f) => {
      if (!f.paga || f.cancelada) return;
      const per = periodoReceita(f);
      if (per.slice(0, 4) !== ano) return;
      const mi = Number(per.split("-")[1]) - 1;
      if (mi >= 0 && mi < 12) meses[mi].receitas += Number(f.total) || 0;
    });
    (contasPagar || []).forEach((c) => {
      if (c.status !== "pago" || !c.dataPagamento) return;
      if (c.dataPagamento.slice(0, 4) !== ano) return;
      const mi = Number(c.dataPagamento.split("-")[1]) - 1;
      if (mi >= 0 && mi < 12) meses[mi].despesas += Number(c.valor) || 0;
      const cat = c.categoria || "Outros";
      despesasPorCategoria[cat] = (despesasPorCategoria[cat] || 0) + (Number(c.valor) || 0);
    });
    meses.forEach((m) => { m.resultado = +(m.receitas - m.despesas).toFixed(2); });
    const totais = meses.reduce((a, m) => ({ receitas: a.receitas + m.receitas, despesas: a.despesas + m.despesas }), { receitas: 0, despesas: 0 });
    totais.resultado = +(totais.receitas - totais.despesas).toFixed(2);
    return { meses, despesasPorCategoria, totais };
  }, [faturas, contasPagar, ano]);

  const chartData = dre.meses.map((m) => ({ label: m.label, Receitas: +m.receitas.toFixed(2), Despesas: +m.despesas.toFixed(2) }));

  const faturasAtivas = (faturas || []).filter((f) => !f.cancelada);

  return (
    <div className="space-y-6" data-testid="relatorios-financeiros-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Relatórios Financeiros</h1>
          <p className="text-slate-400 text-sm mt-0.5">DRE, contas a pagar e contas a receber</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-32">
            <Select value={ano} onValueChange={setAno}>
              <SelectTrigger data-testid="dre-ano"><SelectValue /></SelectTrigger>
              <SelectContent>{anosDisponiveis.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={() => gerarDrePDF({ empresa, ano, ...dre })} data-testid="pdf-dre-btn"><FileDown className="w-4 h-4" /> DRE (PDF)</Button>
        </div>
      </div>

      {/* Resumo do ano */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-500" /> Receitas {ano}</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="dre-receitas">{formatBRL(dre.totais.receitas)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><TrendingDown className="w-4 h-4 text-red-500" /> Despesas {ano}</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="dre-despesas">{formatBRL(dre.totais.despesas)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><Scale className="w-4 h-4 text-blue-500" /> Resultado {ano}</p>
          <p className={`text-2xl font-bold mt-1 ${dre.totais.resultado >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="dre-resultado">{formatBRL(dre.totais.resultado)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{dre.totais.resultado >= 0 ? "Lucro" : "Prejuízo"} (regime de caixa)</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid="dre-grafico">
        <p className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><FileBarChart2 className="w-4 h-4 text-blue-600" /> Receitas x Despesas — {ano}</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} width={54} />
              <Tooltip formatter={(v) => formatBRL(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Receitas" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabela DRE mês a mês */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 font-semibold text-slate-800">DRE mês a mês — {ano}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="px-5 py-3 font-medium">Mês</th>
              <th className="px-5 py-3 font-medium text-right">Receitas</th>
              <th className="px-5 py-3 font-medium text-right">Despesas</th>
              <th className="px-5 py-3 font-medium text-right">Resultado</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {dre.meses.map((m) => (
                <tr key={m.mes} className="hover:bg-slate-50/60">
                  <td className="px-5 py-2.5 font-medium text-slate-700">{m.label}</td>
                  <td className="px-5 py-2.5 text-right text-emerald-600">{formatBRL(m.receitas)}</td>
                  <td className="px-5 py-2.5 text-right text-red-600">{formatBRL(m.despesas)}</td>
                  <td className={`px-5 py-2.5 text-right font-semibold ${m.resultado >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatBRL(m.resultado)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-slate-50 font-bold text-slate-800">
              <td className="px-5 py-3">TOTAL</td>
              <td className="px-5 py-3 text-right text-emerald-700">{formatBRL(dre.totais.receitas)}</td>
              <td className="px-5 py-3 text-right text-red-700">{formatBRL(dre.totais.despesas)}</td>
              <td className={`px-5 py-3 text-right ${dre.totais.resultado >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatBRL(dre.totais.resultado)}</td>
            </tr></tfoot>
          </table>
        </div>
      </div>

      {/* Relatórios detalhados */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center justify-between">
          <div><p className="font-semibold text-slate-800">Contas a Receber</p><p className="text-xs text-slate-400">Todas as faturas (pagas e em aberto)</p></div>
          <Button variant="outline" className="gap-2" onClick={() => gerarContasReceberPDF({ empresa, faturas: faturasAtivas, resById })} data-testid="pdf-receber-btn"><FileDown className="w-4 h-4" /> PDF</Button>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center justify-between">
          <div><p className="font-semibold text-slate-800">Contas a Pagar</p><p className="text-xs text-slate-400">Todas as despesas lançadas</p></div>
          <Button variant="outline" className="gap-2" onClick={() => gerarContasPagarPDF({ empresa, contas: contasPagar, fornById })} data-testid="pdf-pagar-btn"><FileDown className="w-4 h-4" /> PDF</Button>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosFinanceiros;
